/**
 * GlobalDeclareEnumColumnDialog
 *
 * App-level mount for `DeclareEnumColumnDialog`. Driven by the global Zustand
 * store; any UI surface (grid header glyph, filter chip, MetaInfo manager
 * row) opens the dialog by calling `openDeclareEnumColumnDialog(...)`.
 *
 * Behavior on confirm:
 *   1. Pre-fetch values for the chosen source so the picker is warm
 *      (`useDeclareEnumPreview`).
 *   2. Persist the declaration via `addEnumColumn` — plus, in `lookup` mode,
 *      the resolved FK target snapshot and the shared label column (only when
 *      the user touched the dropdown — `useLookupEnumLabelColumn`).
 *      **Or**, when lookup mode's "Mark <target> as a reference table" box is
 *      ticked, persist a `referenceTables` entry and NO column declaration:
 *      the column (and every other column referencing that table) becomes an
 *      *implicit* lookup enum.
 *   3. Invalidate the MetaInfo query so subscribers re-read.
 *   4. Fire `feature:enum_declare` — or `feature:enum_reference_table_declare`
 *      on the reference-table path (never both).
 *   5. Close the dialog.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  DeclareEnumColumnDialog,
  modeToSource,
  type DeclareEnumDialogError,
} from './DeclareEnumColumnDialog';
import { useDeclareEnumColumnDialogStore } from './declareEnumColumnDialogStore';
import {
  useDeclareEnumPreview,
  enumDialogErrorMessage,
} from './useDeclareEnumPreview';
import { useLookupEnumLabelColumn } from './useLookupEnumLabelColumn';
import { useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { addEnumColumn, addEnumGlyphDismissal } from '../../services/metaInfoApi';
import { declareReferenceTable } from '../meta-info/referenceTableActions';
import { useAnalytics } from '../../../../../core/analytics';

const newDeclarationId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `enum-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function GlobalDeclareEnumColumnDialog() {
  const { isOpen, context, closeDialog, setMode } = useDeclareEnumColumnDialogStore();
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();
  // Errors raised by an *action* (declare / dismiss). Sampling errors come
  // from the preview hook and are shown when no action error is pending.
  const [actionError, setActionError] = useState<DeclareEnumDialogError | null>(null);

  const lookupTarget = context?.mode === 'lookup' ? context.lookupTarget : undefined;
  const labelColumn = useLookupEnumLabelColumn({
    connectionId: context?.connectionId ?? null,
    database: context?.database ?? null,
    target: lookupTarget,
    metadataEditors: context?.metadataEditors,
  });
  const preview = useDeclareEnumPreview({
    isOpen,
    context,
    labelColumn: labelColumn.labelColumn,
    labelReady: labelColumn.ready,
  });

  // A fresh context (open / mode switch) clears any stale action error.
  useEffect(() => {
    setActionError(null);
  }, [isOpen, context]);

  const handleClose = useCallback(() => {
    setActionError(null);
    closeDialog();
  }, [closeDialog]);

  const handleConfirm = useCallback(
    async (description: string, options: { asReferenceTable: boolean }) => {
      if (!context) return;
      setActionError(null);
      try {
        const source = modeToSource(context.mode);
        // The label column is shared per target table, so it is written before
        // the declaration — a later refresh re-resolves it from MetaInfo. It is
        // written on the reference-table path too: the flag makes that shared
        // entry the label source for EVERY column pointing at the table.
        if (source === 'lookup') {
          const written = await labelColumn.persistIfTouched();
          if (written) {
            track('feature:loose_fk_display_column_set', {
              value: written,
              source: 'enum_declare',
            });
          }
        }
        // R3 — mark the TARGET table instead of declaring this column. The
        // column becomes an *implicit* lookup enum (no declaration of its own,
        // resolved through `resolveEffectiveLookupDeclaration`), and its pairs
        // are already cached by the dialog's preview fetch.
        if (options.asReferenceTable && source === 'lookup' && context.lookupTarget) {
          // `'cached'` unconditionally: this path only opens when the preview
          // fetched the target's WHOLE pair set within the cap, which is
          // exactly what cached mode promises. The on-demand mode is written
          // by the FK-popover footer, which makes no such check.
          await declareReferenceTable({
            connectionId: context.connectionId,
            database: context.database,
            schema: context.lookupTarget.toSchema,
            table: context.lookupTarget.toTable,
            engine: context.engine,
            mode: 'cached',
            trigger: context.trigger,
            track,
            invalidate,
          });
          handleClose();
          return;
        }
        await addEnumColumn(context.connectionId, context.database, {
          id: newDeclarationId(),
          schema: context.schema,
          table: context.table,
          column: context.column,
          source,
          ...(description ? { description } : {}),
          createdAt: new Date().toISOString(),
          ...(context.path ? { path: context.path, pathKind: context.pathKind ?? 'scalar' } : {}),
          ...(source === 'lookup' && context.lookupTarget
            ? { lookup: context.lookupTarget }
            : {}),
        });
        track('feature:enum_declare', {
          engine: context.engine,
          source,
          trigger: context.trigger,
          on_json_path: !!context.path,
        });
        await invalidate(context.connectionId, context.database);
        handleClose();
      } catch (err) {
        setActionError({
          operation: 'declare',
          message: enumDialogErrorMessage(
            err,
            'Failed to declare as an enum. Check your connection and try again.',
          ),
        });
      }
    },
    [context, handleClose, invalidate, labelColumn, track],
  );

  const handleDismissNotEnum = useCallback(async () => {
    if (!context) return;
    setActionError(null);
    try {
      await addEnumGlyphDismissal(context.connectionId, context.database, {
        schema: context.schema,
        table: context.table,
        column: context.column,
      });
      track('feature:enum_glyph_dismiss', { engine: context.engine });
      await invalidate(context.connectionId, context.database);
      handleClose();
    } catch (err) {
      setActionError({
        operation: 'dismiss',
        message: enumDialogErrorMessage(
          err,
          'Failed to mark the column as not an enum. Please try again.',
        ),
      });
    }
  }, [context, handleClose, invalidate, track]);

  const handleUseSampledInstead = useCallback(() => {
    track('feature:enum_declare_use_sampled', { engine: context?.engine ?? 'unknown' });
    setMode('eligible');
  }, [context, setMode, track]);

  if (!isOpen || !context) return null;

  return (
    <DeclareEnumColumnDialog
      open
      schema={context.schema}
      table={context.table}
      column={context.column}
      dataType={context.dataType}
      engine={context.engine}
      mode={context.mode}
      previewValues={preview.values}
      previewLabels={preview.labels}
      previewLoading={preview.loading}
      previewTruncated={preview.truncated}
      error={actionError ?? preview.error}
      onConfirm={handleConfirm}
      onCancel={handleClose}
      onDismissNotEnum={handleDismissNotEnum}
      path={context.path}
      pathKind={context.pathKind}
      lookupTarget={lookupTarget}
      lookupIneligibleReason={context.lookupIneligibleReason}
      labelColumnValue={labelColumn.selectValue}
      onLabelColumnChange={labelColumn.onChange}
      labelColumnOptions={labelColumn.columnOptions}
      labelColumnLoading={labelColumn.loading}
      onUseSampledInstead={handleUseSampledInstead}
    />
  );
}
